<script setup lang="ts">
/**
 * Text field of Personal data and its dialogs — Figma `Input` (316:4421):
 * the label sits over the value and stands in for the placeholder while the
 * field is empty; underline; error under the field (`04 Field errors`,
 * `11 Change email · error`, `14 Change password · errors`).
 */
import { computed, ref, useId } from 'vue'

interface FairProfileV2TextFieldProps {
  modelValue: string
  label: string
  type?: 'text' | 'email' | 'password'
  inputmode?: 'text' | 'email' | 'numeric'
  autocomplete?: string
  /** Shown under the label while the empty field has focus (e.g. `DD.MM.YYYY`). */
  placeholder?: string
  error?: string
  disabled?: boolean
  maxlength?: number
}

const props = withDefaults(defineProps<FairProfileV2TextFieldProps>(), {
  type: 'text',
  inputmode: undefined,
  autocomplete: 'off',
  placeholder: '',
  error: '',
  disabled: false,
  maxlength: undefined,
})

const emit = defineEmits<{
  'update:modelValue': [value: string]
  blur: []
}>()

const inputId = useId()
const focused = ref(false)
const floated = computed(() => focused.value || Boolean(props.modelValue))
</script>

<template>
  <div
    class="fair-profile-v2-text-field"
    :class="{
      'fair-profile-v2-text-field--focus': focused,
      'fair-profile-v2-text-field--error': Boolean(error),
      'fair-profile-v2-text-field--floated': floated,
      'fair-profile-v2-text-field--disabled': disabled,
    }"
  >
    <label
      class="fair-profile-v2-text-field__box"
      :for="inputId"
    >
      <span class="fair-profile-v2-text-field__label">{{ label }}</span>
      <input
        :id="inputId"
        class="fair-profile-v2-text-field__input type-body-ui"
        :value="modelValue"
        :type="type"
        :inputmode="inputmode"
        :autocomplete="autocomplete"
        :placeholder="floated ? placeholder : ''"
        :disabled="disabled"
        :maxlength="maxlength"
        :aria-invalid="Boolean(error)"
        :aria-describedby="error ? `${inputId}-error` : undefined"
        @input="emit('update:modelValue', ($event.target as HTMLInputElement).value)"
        @focus="focused = true"
        @blur="focused = false; emit('blur')"
      >
    </label>
    <p
      v-if="error"
      :id="`${inputId}-error`"
      class="fair-profile-v2-text-field__error"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>
